// libs
import React from 'react';
import cx from 'classnames';
// dls
import { Text } from '@swiggy-private/dls-web-core/dist/components/Text';
import { GenericIcon } from '@swiggy-private/dls-web-icons/dist/GenericIconWeb';
import { Svg16 as InfoIcon } from '@swiggy-private/dls-web-icons/dist/web/generic/info';
// components
import Table from '@/widgets/Table';
import Tooltip from '@/components/Tooltip';
import CampaginsTablePlaceholder from '@/components/CampaignsTablePlaceholder/CampaginsTablePlaceholder';
// widgets
import * as Cells from './widgets/RowCells';
import Nudge from '@/pages/instamart/campaign/create/widgets/Nudge';
import RecentlyAddedTag from '@/widgets/RecentlyAddedTag';
// constants
import { DlsColorTokens, DlsFontVariants } from '@/constants/DLSTokens';
import { KeywordTableTitles, TABLE_COLUMNS, TABLE_COLUMNS_WITH_METRICS, } from '@/pages/instamart/campaign/create/widgets/Steps/Keywords/constants';
import { Phrases } from '@/pages/instamart/campaign/create/widgets/Nudge/constants';
import { KeywordMatchType } from '@local/typedefs/dist/protos/campaign/Criteria';
// helpers
import { getTableHeaderTooltipAndText } from '@/pages/instamart/campaign/create/widgets/Steps/Keywords/helpers';
// stores
import { CampaignWriteModeEnum } from '@/stores/WriteCampaign/constants';
// hooks
import useNudge from '@/hooks/useNudge';
// styles
import styles from './KeywordTable.module.scss';
function KeywordTable(props) {
    const { mode, selectedKeywords, preSelectedKeywords, suggestedKeywords, updateKeywordByIndex, deleteKeywordByIndex, keywordErrorMessages, tableRef, } = props;
    const { isBroadKeywordNudgeVisible } = useNudge();
    const renderRecentlyAddedBadge = () => React.createElement(RecentlyAddedTag, null);
    const renderRow = (row, index) => {
        var _a, _b;
        const { keywordText, matchType, bid } = row;
        const selectedMatchType = matchType === KeywordMatchType.KEYWORD_MATCH_TYPE_EXACT
            ? KeywordMatchType.KEYWORD_MATCH_TYPE_EXACT
            : KeywordMatchType.KEYWORD_MATCH_TYPE_BROAD;
        const selectedSuggestedKeyword = suggestedKeywords.find((suggestedKeyword) => suggestedKeyword.keywordText === keywordText);
        const { bid: { minimumBid, suggestedLowerBid, suggestedUpperBid }, searchCount, } = (_a = selectedSuggestedKeyword === null || selectedSuggestedKeyword === void 0 ? void 0 : selectedSuggestedKeyword[selectedMatchType]) !== null && _a !== void 0 ? _a : { bid: {} };
        const shouldShowRecentlyAddedBadge = mode === CampaignWriteModeEnum.EDIT &&
            !preSelectedKeywords.some((item) => row.keywordText === item.keywordText);
        const errorMessage = keywordErrorMessages === null || keywordErrorMessages === void 0 ? void 0 : keywordErrorMessages[row.keywordText];
        const handleMatchTypeChange = (matchType) => {
            handleUpdateRow({ matchType });
        };
        const handleBidChange = (bid) => {
            handleUpdateRow({ bid: parseFloat(bid) });
        };
        const handleUpdateRow = (changes) => {
            const keywordIndex = selectedKeywords.findIndex((kw) => kw.keywordText === keywordText);
            updateKeywordByIndex(changes, keywordIndex, 0);
        };
        const handleRemoveSelectedRow = () => {
            deleteKeywordByIndex(index);
        };
        return (React.createElement("ul", { key: index, className: styles.kwTableRow },
            shouldShowRecentlyAddedBadge && renderRecentlyAddedBadge(),
            React.createElement(Cells.KeywordTextCell, { column: mode === CampaignWriteModeEnum.EDIT
                    ? TABLE_COLUMNS_WITH_METRICS[0]
                    : TABLE_COLUMNS[0], keywordText: keywordText }),
            React.createElement(Cells.SearchVolumeCell, { column: mode === CampaignWriteModeEnum.EDIT
                    ? TABLE_COLUMNS_WITH_METRICS[1]
                    : TABLE_COLUMNS[1], searchCount: searchCount }),
            mode === CampaignWriteModeEnum.EDIT && (React.createElement(Cells.ImpressionCell, { column: TABLE_COLUMNS_WITH_METRICS[2], impressions: (_b = row.metrics) === null || _b === void 0 ? void 0 : _b.impressions })),
            React.createElement(Cells.MinimumBidCell, { column: mode === CampaignWriteModeEnum.EDIT
                    ? TABLE_COLUMNS_WITH_METRICS[3]
                    : TABLE_COLUMNS[2], minimumBid: minimumBid }),
            React.createElement(Cells.MatchTypeSelectorCell, { column: mode === CampaignWriteModeEnum.EDIT
                    ? TABLE_COLUMNS_WITH_METRICS[4]
                    : TABLE_COLUMNS[3], keyword: row, matchType: matchType, handleMatchTypeChange: handleMatchTypeChange }),
            React.createElement(Cells.BidInputFieldCell, { column: mode === CampaignWriteModeEnum.EDIT
                    ? TABLE_COLUMNS_WITH_METRICS[5]
                    : TABLE_COLUMNS[4], bid: bid, minimumBid: minimumBid, suggestedLowerBid: suggestedLowerBid, suggestedUpperBid: suggestedUpperBid, handleBidChange: handleBidChange, errorMessage: errorMessage }),
            React.createElement(Cells.CrossButtonCell, { column: mode === CampaignWriteModeEnum.EDIT
                    ? TABLE_COLUMNS_WITH_METRICS[6]
                    : TABLE_COLUMNS[5], handleRemoveSelectedRow: handleRemoveSelectedRow })));
    };
    const renderColHeader = ({ title, width }) => {
        const centerAlign = title !== KeywordTableTitles.TABLE_HEADERS.KEYWORD;
        const { showTooltip, tooltipText } = getTableHeaderTooltipAndText(title);
        return (React.createElement("div", { className: cx(styles.kwTableHeaderCell, {
                [styles.kwTableHeaderCellCenterAlign]: centerAlign,
            }), style: {
                width,
            } },
            React.createElement(Text, { variant: DlsFontVariants.body_B2_Bold, color: DlsColorTokens.text_Low_Emphasis, className: styles.kwTableHeaderText }, title),
            showTooltip && (React.createElement(Tooltip, { content: tooltipText },
                React.createElement(GenericIcon, { Icon: InfoIcon, className: styles.kwTableHeaderIcon, fillColor: DlsColorTokens.text_Low_Emphasis })))));
    };
    const renderTitle = () => {
        return (React.createElement("div", { className: styles.title },
            React.createElement("div", { className: styles.text },
                React.createElement(Text, { variant: DlsFontVariants.button_Text_BT2, color: DlsColorTokens.text_Highest_Emphasis, className: styles.keywordTableTitle }, KeywordTableTitles.TITLE),
                React.createElement(Text, { variant: DlsFontVariants.body_B1_Reg, color: DlsColorTokens.text_Med_Emphasis }, KeywordTableTitles.SUBTITLE)),
            isBroadKeywordNudgeVisible && (React.createElement("div", { className: styles.nudge },
                React.createElement(Nudge, { text: Phrases.BROAD_KEYWORD })))));
    };
    const renderPlaceholder = () => {
        return (React.createElement(CampaginsTablePlaceholder, { title: "No keywords selected", subTitle: "You need to select keyword(s) to proceed further" }));
    };
    const keywordColumns = mode === CampaignWriteModeEnum.EDIT
        ? TABLE_COLUMNS_WITH_METRICS
        : TABLE_COLUMNS;
    return (React.createElement("div", { className: styles.kwTableContainer },
        renderTitle(),
        React.createElement(Table, { data: selectedKeywords, columns: keywordColumns, renderRow: renderRow, renderHeader: renderColHeader, onSort: () => void 0, classNames: {
                container: styles.kwTable,
                header: styles.kwTableHeader,
                tableBody: styles.kwTableBody,
            }, renderPlaceholder: renderPlaceholder, rowContainerRef: tableRef })));
}
export default KeywordTable;
